import { assign, fromPromise, setup } from "xstate-5";
import { InjectionKey } from "vue";
import { useMachine } from "@xstate-5/vue";
import {
  DTOProductData,
  DTOSingleProductAvailabilityStatus,
  DTOTrackerProductStatisticResponse,
  DTOTrackerProductsStatusResponse,
} from "vue-app/api/dto";
import {
  getRecommendationTrackerProductStatus,
  GetRecommendationTrackerProductStatusPayload,
  getTrackerProductAvailabilityStatus,
  getTrackerProductInfoV2,
  getTrackerProductStatisticV2,
  setRecommendationTrackerProductStatus,
  SetRecommendationTrackerProductStatusPayload,
} from "vue-app/api/CategoryService";
import { AxiosError } from "axios";
import { xlog } from "vue-app/utils/xstate";
import { logger } from "vue-app/utils/logger";
import dayjs from "dayjs";
import utc from "dayjs/plugin/utc";
dayjs.extend(utc);
import { getUnixEndOfDay, getUnixStartOfDayPlusOneSecond } from "vue-app/datesDatepicker/datesDatepicker";
import { sendCustomEventToGA } from "vue-app/plugins/edroneGtag";
import { PRODUCT_RECOMMENDATION_STATUS } from "vue-app/components/ProductReport/ProductReport.enum";
import { mapProductStatusEnumToProductStatusNumber } from "vue-app/components/ProductReport/ProductReport.util";

export interface ProductCategories {}

type ProductReportContext = {
  appIdProductId: string | null;
  trackerProductId: string | null;
  dateFrom: number;
  dateTo: number;
  productInfo: DTOProductData | null;
  statistics: DTOTrackerProductStatisticResponse | null;
  isAvailable: boolean;
  productStatus: PRODUCT_RECOMMENDATION_STATUS;
};

type OpenDrawerPayload = { appIdProductId: string; trackerProductId: string };

const initialContext: ProductReportContext = {
  appIdProductId: null,
  trackerProductId: null,
  productInfo: null,
  statistics: null,
  dateFrom: dayjs.utc().subtract(30, "day").startOf("day").unix(),
  dateTo: dayjs().subtract(1, "day").endOf("day").unix(),
  isAvailable: false,
  productStatus: PRODUCT_RECOMMENDATION_STATUS.DEFAULT,
};

export const productReportMachine = setup({
  types: {
    tags: {} as "fetchingProductInformation" | "fetchingProductStatistics" | "refetchProductsStatistics",
    context: {} as ProductReportContext,
    events: {} as
      | { type: "ASSIGN_INITIAL_DATA"; payload: OpenDrawerPayload }
      | { type: "RESET_CONTEXT" }
      | { type: "CHANGE_DATE_RANGE"; payload: { dateFrom: Date; dateTo: Date } }
      | { type: "CHANGE_RECOMMENDATION_STATUS"; payload: PRODUCT_RECOMMENDATION_STATUS }
      | { type: "xstate.done.actor.fetchProductInfo"; output: DTOProductData }
      | { type: "xstate.done.actor.fetchProductStatistics"; output: DTOTrackerProductStatisticResponse }
      | { type: "xstate.done.actor.fetchProductAvailability"; output: DTOSingleProductAvailabilityStatus[] }
      | { type: "xstate.done.actor.fetchRecommendationProductStatus"; output: DTOTrackerProductsStatusResponse }
      | { type: "xstate.done.actor.setRecommendationProductStatus"; output: void }
      | { type: "xstate.error.actor.fetchProductInfo"; error: AxiosError }
      | { type: "xstate.error.actor.fetchProductStatistics"; error: AxiosError }
      | { type: "xstate.error.actor.fetchProductAvailability"; error: AxiosError }
      | { type: "xstate.error.actor.fetchRecommendationProductStatus"; error: AxiosError }
      | { type: "xstate.error.actor.setRecommendationProductStatus"; error: AxiosError },
  },
  actions: {
    assignInitialData: assign(({ event, context }, params: OpenDrawerPayload) => {
      xlog({ label: "assignInitialData", type: "action", event, context });

      sendCustomEventToGA("product-report-drawer-open", {
        appIdProductId: params.appIdProductId,
        trackerProductId: params.trackerProductId,
      });

      return {
        ...context,
        appIdProductId: params.appIdProductId,
        trackerProductId: params.trackerProductId,
      };
    }),
    assignProductInfo: assign(({ event, context }, params: DTOProductData) => {
      xlog({ label: "assignProductInfo", type: "action", event, context });

      return {
        ...context,
        productInfo: params,
      };
    }),
    assignProductStatistics: assign(({ event, context }, params: DTOTrackerProductStatisticResponse) => {
      xlog({ label: "assignProductStatistics", type: "action", event, context });

      return {
        ...context,
        statistics: params,
      };
    }),
    assignRecommendationProductStatus: assign(({ event, context }, params: PRODUCT_RECOMMENDATION_STATUS) => {
      xlog({ label: "assignRecommendationProductStatus", type: "action", event, context, params });

      return {
        ...context,
        productStatus: params,
      };
    }),
    resetContext: assign(({ event, context }) => {
      xlog({ label: "resetContext", type: "action", event, context });

      return initialContext;
    }),
    assignProductsAvailability: assign(({ event, context }, availabilityData: DTOSingleProductAvailabilityStatus[]) => {
      xlog({ label: "assignProductsAvailability", type: "action", event, context });

      const isAvailable = availabilityData.some((availability) => availability.isAvailable);

      return {
        ...context,
        isAvailable,
      };
    }),
    assignDate: assign(({ event, context }, params: { dateFrom: Date; dateTo: Date }) => {
      xlog({ label: "assignDate", type: "action", event, context });

      const dateFrom = getUnixStartOfDayPlusOneSecond(params.dateFrom);
      const dateTo = getUnixEndOfDay(params.dateTo);

      sendCustomEventToGA("product-report-drawer-change-date", {
        dateFrom: dateFrom,
        dateTo: dateTo,
      });

      return {
        ...context,
        dateFrom,
        dateTo,
      };
    }),
    showToast: ({ context, event }) => {
      xlog({ label: "showToast", type: "action", context, event });
      const { toast } = window._trans._products.content.drawer.status;

      switch (event.type) {
        case "xstate.error.actor.setRecommendationProductStatus":
          window.showToast(toast.error, "error");
          break;
        case "xstate.done.actor.setRecommendationProductStatus":
          window.showToast(toast.success, "success");
          break;
      }
    },
  },
  actors: {
    fetchProductInfo: fromPromise(
      async ({ input }: { input: Parameters<typeof getTrackerProductInfoV2> }) =>
        await getTrackerProductInfoV2(...input)
    ),
    fetchProductStatistics: fromPromise(
      async ({ input }: { input: Parameters<typeof getTrackerProductStatisticV2> }) =>
        await getTrackerProductStatisticV2(...input)
    ),
    fetchProductAvailability: fromPromise(
      async ({ input }: { input: Parameters<typeof getTrackerProductAvailabilityStatus> }) =>
        await getTrackerProductAvailabilityStatus(...input)
    ),
    fetchRecommendationProductStatus: fromPromise(
      async ({ input }: { input: GetRecommendationTrackerProductStatusPayload }) => {
        return await getRecommendationTrackerProductStatus(input);
      }
    ),
    setRecommendationProductStatus: fromPromise(
      async ({ input }: { input: SetRecommendationTrackerProductStatusPayload }) => {
        return await setRecommendationTrackerProductStatus(input);
      }
    ),
  },
}).createMachine({
  context: initialContext,
  id: "productReportMachine",
  initial: "drawerClose",
  states: {
    drawerClose: {
      on: {
        ASSIGN_INITIAL_DATA: {
          target: "fetchingRequiredData",
          actions: [{ type: "assignInitialData", params: ({ event }) => event.payload }],
        },
      },
    },

    fetchingRequiredData: {
      type: "parallel",
      states: {
        fetchProductInformation: {
          tags: ["fetchingProductInformation"],
          states: {
            pending: {
              invoke: {
                src: "fetchProductInfo",
                id: "fetchProductInfo",
                input: ({ context }) => {
                  if (!context.trackerProductId) {
                    logger({
                      message: "Missing trackerProductId when fetching product info",
                      level: "error",
                      extra: { context },
                    });
                  }

                  return [context.trackerProductId as string];
                },
                onDone: {
                  target: "fetchingProductAvailability",
                  actions: [
                    {
                      type: "assignProductInfo",
                      params: ({ event }) => event.output,
                    },
                  ],
                },
                onError: "done",
              },
            },

            done: {
              type: "final",
            },

            fetchingProductAvailability: {
              invoke: {
                src: "fetchProductAvailability",
                id: "fetchProductAvailability",
                input: ({ context }) => {
                  return [[Number(context.trackerProductId)]];
                },
                onDone: {
                  target: "done",
                  actions: [{ type: "assignProductsAvailability", params: ({ event }) => event.output.data }],
                },
                onError: "done",
              },
            },
          },
          initial: "pending",
        },
        fetchProductStatistics: {
          tags: ["fetchingProductStatistics"],
          states: {
            pending: {
              invoke: {
                src: "fetchProductStatistics",
                id: "fetchProductStatistics",
                input: ({ context }) => {
                  if (!context.appIdProductId) {
                    logger({
                      message: "Missing appIdProductId when fetching product statistics",
                      level: "error",
                      extra: { context },
                    });
                  }

                  return [context.appIdProductId as string, context.dateFrom, context.dateTo];
                },
                onDone: {
                  target: "done",
                  actions: [
                    {
                      type: "assignProductStatistics",
                      params: ({ event }) => event.output,
                    },
                  ],
                },
                onError: "done",
              },
            },
            done: {
              type: "final",
            },
          },
          initial: "pending",
        },
        fetchRecommendationProductStatus: {
          states: {
            pending: {
              invoke: {
                src: "fetchRecommendationProductStatus",
                id: "fetchRecommendationProductStatus",
                input: ({ context }) => {
                  if (!context.trackerProductId) {
                    logger({
                      message: "Missing trackerProductId when fetching recommendation product status",
                      level: "error",
                      extra: { context },
                    });

                    throw new Error("Missing trackerProductId");
                  }

                  return {
                    trackerProductIds: [context.trackerProductId],
                  };
                },
                onDone: [
                  {
                    target: "done",
                    actions: [
                      {
                        type: "assignRecommendationProductStatus",
                        params: ({ event }) =>
                          (event.output.data[0]?.statusName as PRODUCT_RECOMMENDATION_STATUS) ||
                          PRODUCT_RECOMMENDATION_STATUS.DEFAULT,
                      },
                    ],
                  },
                ],
              },
            },
            done: {
              type: "final",
            },
          },
          initial: "pending",
        },
      },
      onDone: {
        target: "#productReportMachine.idle",
      },
    },

    idle: {
      on: {
        RESET_CONTEXT: {
          target: "drawerClose",
          actions: "resetContext",
        },
        CHANGE_DATE_RANGE: {
          target: "refetchProductsStatistics",
          actions: [
            {
              type: "assignDate",
              params: ({ event }) => event.payload,
            },
          ],
        },
        CHANGE_RECOMMENDATION_STATUS: {
          target: "setRecommendationProductStatus",
          actions: [
            {
              type: "assignRecommendationProductStatus",
              params: ({ event }) => event.payload,
            },
          ],
        },
      },
    },

    setRecommendationProductStatus: {
      invoke: {
        src: "setRecommendationProductStatus",
        id: "setRecommendationProductStatus",

        input: ({ context }) => {
          return {
            trackerProductIds: [context.trackerProductId as string],
            status: mapProductStatusEnumToProductStatusNumber(context.productStatus),
          };
        },

        onDone: [{ target: "idle", actions: "showToast" }],
        onError: [{ target: "idle", actions: "showToast" }],
      },
    },

    refetchProductsStatistics: {
      tags: ["refetchProductsStatistics"],
      invoke: {
        src: "fetchProductStatistics",
        id: "fetchProductStatistics",
        input: ({ context }) => {
          if (!context.appIdProductId) {
            logger({
              message: "Missing appIdProductId when refetchProductsStatistics product statistics",
              level: "error",
              extra: { context },
            });
          }

          return [context.appIdProductId as string, context.dateFrom, context.dateTo];
        },
        onError: "idle",
        onDone: {
          target: "idle",
          actions: [
            {
              type: "assignProductStatistics",
              params: ({ event }) => event.output,
            },
          ],
        },
      },
    },
  },
});

export const symbolProductReportMachine: InjectionKey<ReturnType<typeof useMachine<typeof productReportMachine>>> =
  Symbol();
